<template>
    <div class="PaletteSelector">
        <ul class="PaletteBuilder__selector">
            <li
                v-for="(palette, index) in palettes"
                :key="index"
                class="Palette"
            >
                <input
                    :id="'palette' + index"
                    name="palette[]"
                    class="Palette__input"
                    type="radio"
                    :value="palette.id"
                    v-model="paletteId"
                    @change="onPaletteSelect()"
                >
                <label
                    :for="'palette' + index"
                    class="Palette__label"
                >
                    <span
                        class="Palette__color Palette__color--1"
                        :style="'background-color:' + palette.color_1"
                    />
                    <span
                        class="Palette__color Palette__color--2"
                        :style="'background-color:' + palette.color_2"
                    />
                    <span
                        class="Palette__color Palette__color--3"
                        :style="'background-color:' + palette.color_3"
                    />
                </label>
                <span class="PaletteSelector__selected">
                    <i class="fas fa-check-circle" />
                </span>
            </li>
        </ul>

        <div class="PaletteBuilder__custom">
            <div class="PaletteBuilder__custom__title">
                <h3 class="PaletteBuilder__custom__title">
                    Selected Colors
                </h3>
                <HelpModal
                    v-if="helpText"
                    :text="helpText"
                    :title="helpTitle"
                />
            </div>
            <p
                class="PaletteBuilder__upsell"
                v-if="!canAccessCustomColors"
                v-html="customColorUpsell"
            />
            <div class="PaletteBuilder__custom__colors">
                <div class="PaletteBuilder__custom__color">
                    <div class="PaletteBuilder__custom__color__input">
                        <v-text-field
                            label="Color 1"
                            outlined
                            v-model="color1"
                            @change="onChangeCustom"
                            :readonly="!canAccessCustomColors"
                            :aria-readonly="!canAccessCustomColors"
                            :class="{jsReadOnly : !canAccessCustomColors}"
                        />
                    </div>
                    <div class="PaletteBuilder__custom__color__swatch__wrap">
                        <div
                            class="PaletteBuilder__custom__color__swatch"
                            :style="'background-color:' + color1"
                        />
                    </div>
                </div>

                <div class="PaletteBuilder__custom__color">
                    <div class="PaletteBuilder__custom__color__input">
                        <v-text-field
                            label="Color 2"
                            outlined
                            v-model="color2"
                            @change="onChangeCustom"
                            :readonly="!canAccessCustomColors"
                            :aria-readonly="!canAccessCustomColors"
                            :class="{jsReadOnly : !canAccessCustomColors}"
                        />
                    </div>
                    <div class="PaletteBuilder__custom__color__swatch__wrap">
                        <div
                            class="PaletteBuilder__custom__color__swatch"
                            :style="'background-color:' + color2"
                        />
                    </div>
                </div>

                <div class="PaletteBuilder__custom__color">
                    <div class="PaletteBuilder__custom__color__input">
                        <v-text-field
                            label="Color 3"
                            outlined
                            v-model="color3"
                            @change="onChangeCustom"
                            :readonly="!canAccessCustomColors"
                            :aria-readonly="!canAccessCustomColors"
                            :class="{jsReadOnly : !canAccessCustomColors}"
                        />
                    </div>
                    <div class="PaletteBuilder__custom__color__swatch__wrap">
                        <div
                            class="PaletteBuilder__custom__color__swatch"
                            :style="'background-color:' + color3"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SubmissionBoilerplate from '@/views/Public/Field/Fields/SubmissionBoilerplate';
import store from '@/store/store';
import { EventBus } from '@/store/eventBus';
import { mapActions, mapMutations } from 'vuex';

export default {
    mixins: [SubmissionBoilerplate],

    props: {
        cardNamespace: {
            type: String,
            required: false
        }
    },

    data() {
        return {
            palette: []
        };
    },

    computed: {
        role() {
            return store.state.auth.user.role;
        },

        palettes() {
            return store.state.websiteDesign.palettes;
        },

        paletteId: {
            get() {
                return store.state.websiteDesign.paletteId;
            },

            set(value) {
                this.setPaletteId(value);
            }
        },

        color1: {
            get() {
                return store.state.websiteDesign.color1;
            },

            set(value) {
                this.setColor1(value);
            }
        },


        color2: {
            get() {
                return store.state.websiteDesign.color2;
            },

            set(value) {
                this.setColor2(value);
            }
        },

        color3: {
            get() {
                return store.state.websiteDesign.color3;
            },

            set(value) {
                this.setColor3(value);
            }
        },

        customColorUpsell() {
            return store.state[this.fieldNamespace].options.customColorUpsell;
        },

        customColorAccessLevel() {
            return store.state[this.fieldNamespace].options.customColorAccessLevel;
        },

        canAccessCustomColors() {
            if (this.role === 'basic') {
                return this.customColorAccessLevel === 'basic';
            }

            if (this.role === 'premium') {
                return this.customColorAccessLevel === 'basic'
                    || this.customColorAccessLevel === 'premium';
            }

            if (this.role === 'plus') {
                return this.customColorAccessLevel === 'basic'
                    || this.customColorAccessLevel === 'premium'
                    || this.customColorAccessLevel === 'plus';
            }

            return false;
        }
    },

    methods: {
        ...mapMutations({
            setPalettes: 'websiteDesign/setPalettes',
            setPaletteId: 'websiteDesign/setPaletteId',
            setColor1: 'websiteDesign/setColor1',
            setColor2: 'websiteDesign/setColor2',
            setColor3: 'websiteDesign/setColor3'
        }),

        ...mapActions({
            getAllPalettes: 'websiteDesign/getAllPalettes'
        }),

        loadFieldData() {
            this.getAllPalettes().then(() => {
                this.loadSelected()
                    .then(() => {
                        this.setFieldData();
                    })
                    .catch(() => {
                        this.loadCustom()
                            .then(() => {
                                this.setFieldData();
                            })
                            .catch(() => {
                                this.setDefaults()
                                    .then(() => {
                                        this.setFieldData();
                                    })
                                    .catch((id) => { // First palette ID in this.palettes.
                                        this.setPaletteId(id);
                                        this.onPaletteSelect();
                                    });
                            });
                    });
            });
        },

        setDefaults() {
            return new Promise((resolve, reject) => {
                if (!this.paletteId &&
                    !this.color1 &&
                    !this.color2 &&
                    !this.color3
                ) {
                    reject(this.palettes[0].id);
                }
                resolve();
            });
        },

        loadSelected() {
            return new Promise((resolve, reject) => {
                if (this.submission && this.submission.paletteId) {
                    this.setPaletteId(this.submission.paletteId);
                    const palette = this.getPaletteColorsByPaletteId(this.paletteId);
                    this.setColor1(palette.color_1);
                    this.setColor2(palette.color_2);
                    this.setColor3(palette.color_3);
                    resolve();
                    return;
                }

                reject();
            });
        },

        loadCustom() {
            return new Promise((resolve, reject) => {
                if (this.role !== this.customColorAccessLevel) {
                    reject();
                    return;
                }

                if (this.submission && this.submission.customPalette) {
                    this.setColor1(this.submission.customPalette.color_1);
                    this.setColor2(this.submission.customPalette.color_2);
                    this.setColor3(this.submission.customPalette.color_3);
                    this.setPaletteId(null);
                    resolve();
                    return;
                }
                reject();
            });
        },

        getPaletteColorsByPaletteId(id) {
            return this.palettes.find(obj => {
                return obj.id === id;
            });
        },

        onPaletteSelect() {
            if (this.paletteId) {
                const palette = this.getPaletteColorsByPaletteId(this.paletteId);
                this.setColor1(palette.color_1);
                this.setColor2(palette.color_2);
                this.setColor3(palette.color_3);
                this.setFieldData();
            }
        },

        onChangeCustom() {
            if (this.role !== this.customColorAccessLevel) {
                return;
            }

            this.setColor1(this.color1);
            this.setColor2(this.color2);
            this.setColor3(this.color3);
            this.setPaletteId(null);
            this.setFieldData();
        },

        setFieldData() {
            this.fieldData = {
                paletteId: this.paletteId,
                customPalette: {
                    color_1: this.color1,
                    color_2: this.color2,
                    color_3: this.color3
                }
            };

            this.storeFieldData();
            EventBus.$emit('UserPreviewChange', true);
        }
    }
};
</script>
<style lang="scss">
.PaletteBuilder__selector {
    list-style: none;
    margin: 0 0 48px !important;
    padding: 0 !important;
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
}

.Palette {
    position: relative;
    height: 30px;
    margin-right: 2px;
    margin-bottom: 2px;
}

.Palette__label {
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-flow: row nowrap;
    border-radius: $border-radius-root;
    cursor: pointer;
}

.Palette__input {
    position: absolute;
    z-index: 0;
    cursor: pointer;
}

.PaletteSelector__selected {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3;
    display: flex;
    flex: 0 0 100%;
    width: 100%;
    justify-content: center;
    align-content: center;
    align-items: center;
    border-radius: $border-radius-root;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s linear;

    i {
        position: absolute;
        top: -4px;
        right: -4px;
        color: $primary-500;
        font-size: 1rem;
        text-shadow: 2px 2px 2px rgba(black, 0.2);
    }
}

.Palette__input:checked {
    & ~ .PaletteSelector__selected {
        opacity: 1;
        transition: opacity 0.2s linear;
    }
}

.Palette__color {
    flex: 0 0 33.333%;
    width: 33.333%;
    height: 100%;
    box-shadow: 2px 2px 2px rgba(black, 0.2);
}

.Palette__color--1 {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
}

.Palette__color--3 {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
}

.PaletteBuilder__custom__title {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    flex: 0 0 100%;
    width: 100%;

    h3 {
        flex: 0 0 auto;
        width: auto;
    }
}

.PaletteBuilder__upsell {
    margin-bottom: 0 !important;
    font-size: 0.9rem;
    font-style: italic;
}

.PaletteBuilder__custom__colors {
    display: flex;
    justify-content: space-between;
    flex-flow: row wrap;
    flex: 0 0 100%;
    margin-top: 24px !important;
}

.PaletteBuilder__custom__color {
    display: flex;
    flex-flow: row nowrap;
    flex: 0 0 100%;
    width: 100%;

    @media (min-width: 768px) {
        flex: 0 0 32%;
        width: 32%;
    }
}

.PaletteBuilder__custom__color__swatch__wrap {
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-content: center;
    align-items: center;
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    margin-left: 12px;
}

.PaletteBuilder__custom__color__swatch {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    border-radius: 24px;
    box-shadow: 2px 2px 2px rgba(black, 0.2);
}

.PaletteBuilder__custom__color__input {
    flex: 1 1 80%;
    width: 80%;
    height: 100%;

    .jsReadOnly {
        .v-input__slot {
            background-color: #eaeaea;
        }
    }
}
</style>
